import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import PointsHistoryTable from "@/components/customer/tables/points-history-table";
import NotesDialog from "@/components/customer/dialog/notes-dialog";
import useDebounce from "@/hooks/useDebounce";
import Paginat from "@/components/lib/paginat";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import { fetchCustomerPointsHistory } from "@/redux/slice/customer-slice/customer-slice";
import { formatCurrency } from "@/lib/utils";

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return formatCurrency(0, "GBP");
  }

  const numericValue =
    typeof value === "string"
      ? Number(value.replace(/[^\d.-]/g, ""))
      : Number(value);

  return formatCurrency(Number.isNaN(numericValue) ? 0 : numericValue, "GBP");
};

function PointsHistoryTab({ customerId, restaurantId }) {
  const dispatch = useDispatch();
  const {
    pointsHistories,
    pointsHistoryPagination,
    pointsHistoryLoading,
  } = useSelector((state) => state.customer.customer);

  const [searchInput, setSearchInput] = useState("");
  const [orderType, setOrderType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [notesDialog, setNotesDialog] = useState(false);
  const [activeNote, setActiveNote] = useState("");

  const debouncedSearch = useDebounce(searchInput, 500);

  useEffect(() => {
    if (!customerId || !restaurantId) return;

    dispatch(
      fetchCustomerPointsHistory({
        restaurantId,
        customerId,
        page: currentPage,
        per_page: perPage,
        search_term: debouncedSearch,
        order_type: orderType === "all" ? "" : orderType,
        sort_field: "created_at",
        sort_order: "desc",
      }),
    );
  }, [
    dispatch,
    restaurantId,
    customerId,
    currentPage,
    perPage,
    debouncedSearch,
    orderType,
  ]);

  const rows = useMemo(
    () =>
      (pointsHistories || []).map((item) => ({
        id: item?.id,
        date: item?.date || "N/A",
        orderId:
          item?.order_id !== undefined ? `Order ID: ${item.order_id}` : "Order ID: N/A",
        orderType: item?.order_type || "N/A",
        orderPrice: formatPrice(item?.order_price),
        discount: formatPrice(0),
        loyalty: item?.points_code || "N/A",
        earnPoints: item?.earned_points ?? 0,
        usedPoints: item?.used_points ?? 0,
        pointsAmount: formatPrice(item?.points_value),
        notes: item?.points_code || "No notes available",
        operator: item?.operator || "N/A",
      })),
    [pointsHistories],
  );

  const handleOpenNotes = (note) => {
    setActiveNote(note);
    setNotesDialog(true);
  };

  return (
    <div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Input
          value={searchInput}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchInput(e.target.value);
          }}
          placeholder="Search by Order ID"
          className="max-w-[310px] bg-cblack3 text-cwhite"
        />

        <div className="flex items-center gap-4">
          <Select
            value={orderType}
            onValueChange={(value) => {
              setCurrentPage(1);
              setOrderType(value);
            }}
          >
            <SelectTrigger className="w-[240px] bg-cblack3 text-cwhite">
              <SelectValue placeholder="Filter by Order Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Filter by Order Type</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
              <SelectItem value="in house">In house</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 text-sm text-cwhite">
            <span>Show</span>
            <Select
              value={String(perPage)}
              onValueChange={(value) => {
                setCurrentPage(1);
                setPerPage(Number(value));
              }}
            >
              <SelectTrigger className="w-[84px] bg-cblack3 text-cwhite">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="40">40</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {pointsHistoryLoading ? (
        <OnlyTableSekeleton header={false} row={perPage} columns={11} />
      ) : (
        <PointsHistoryTable rows={rows} onOpenNotes={handleOpenNotes} />
      )}

      <Paginat
        pagination={pointsHistoryPagination}
        onPageChange={setCurrentPage}
        className="pt-3"
      />

      <NotesDialog isOpen={notesDialog} onClose={setNotesDialog} note={activeNote} />
    </div>
  );
}

export default PointsHistoryTab;
